/**
 * Character builder. Layered: body shape, head/hat, face, body hue,
 * accent hue. Lives preview lives in the parent (CharacterCreate).
 */
import { IconAvatar, type IconValue } from './IconAvatar';
import { BODY_SHAPES, HAT_SHAPES, FACE_SHAPES, HUES } from './characterParts';
import type { CosmeticInventory } from '../../lib/api';

/** Body archetypes (Phase 2) — drawn by the Pixi renderer at idx 100+.
 *  The IconAvatar SVG can't render these yet, so the picker shows a
 *  small palette swatch as a placeholder. The id matches drawBody. */
const BODY_ARCHETYPES: Array<{ id: number; label: string; color: string; rarity: 3 | 4 }> = [
  { id: 100, label: 'Floating Orb', color: 'hsl(28 60% 56%)',  rarity: 3 },
  { id: 101, label: 'Eight-Legged', color: 'hsl(290 50% 48%)', rarity: 3 },
  { id: 102, label: 'Phantom',      color: 'hsl(220 30% 70%)', rarity: 4 },
  { id: 103, label: 'Crystal',      color: 'hsl(180 45% 60%)', rarity: 4 },
  { id: 104, label: 'Living Flame', color: 'hsl(20 80% 60%)',  rarity: 4 },
  { id: 105, label: 'Hexagon',      color: 'hsl(140 50% 50%)', rarity: 4 },
];

/** 3-color palette catalog. Mirrors PALETTES in shared/character-render
 *  but rendered as gradient swatches for the picker. */
const PALETTE_SWATCHES: Array<{ id: string; label: string; gradient: string; rarity: 2 | 3 | 4 }> = [
  { id: 'palette-dusk',      label: 'Dusk',      gradient: 'linear-gradient(135deg,#3b1452 0%,#a05078 50%,#dec07a 100%)',   rarity: 2 },
  { id: 'palette-bone',      label: 'Bone',      gradient: 'linear-gradient(135deg,#e6dfd0 0%,#88858f 50%,#3a3742 100%)',   rarity: 3 },
  { id: 'palette-emerald',   label: 'Emerald',   gradient: 'linear-gradient(135deg,#176e3e 0%,#e2c33b 60%,#cae6c2 100%)',   rarity: 3 },
  { id: 'palette-prismatic', label: 'Prismatic', gradient: 'conic-gradient(from 0deg, #ff4a2a, #e2c33b, #6abf3f, #4a9eff, #9b6dff, #ff4a8a, #ff4a2a)', rarity: 4 },
];

interface Props {
  value: IconValue;
  onChange: (next: IconValue) => void;
  inventory?: CosmeticInventory;
  onInspectLocked?: (item: LockedCosmeticInfo) => void;
}

export interface LockedCosmeticInfo {
  kind: NonNullable<CosmeticInventory['unlockHints']>[number]['kind'];
  id: number | string;
  label: string;
  hint: string;
  progress?: { current: number; target: number; label: string };
}

export function IconPicker({ value, onChange, inventory, onInspectLocked }: Props) {
  // Coerce legacy values into extended.
  const v: IconValue = {
    set: 'tavern-2',
    id: 0,
    hue: value.hue ?? 22,
    body: value.body ?? 0,
    hat: value.hat ?? 0,
    face: value.face ?? 0,
    accentHue: value.accentHue ?? ((value.hue ?? 22) + 60) % 360,
    ...(value.paletteId ? { paletteId: value.paletteId } : {}),
  };
  function update(patch: Partial<IconValue>): void {
    onChange({ ...v, ...patch });
  }
  const canBody = (i: number) => !inventory || inventory.bodies.includes(i);
  const canHat = (i: number) => !inventory || inventory.hats.includes(i);
  const canFace = (i: number) => !inventory || inventory.faces.includes(i);
  const canHue = (h: number) => !inventory || inventory.hues.includes(h);
  const canAccentHue = (h: number) => !inventory || inventory.accentHues.includes(h);
  const canPalette = (id: string) => !inventory || (inventory.palettes ?? []).includes(id);
  const hintFor = (kind: LockedCosmeticInfo['kind'], id: number | string, label: string): LockedCosmeticInfo => (
    inventory?.unlockHints?.find((h) => h.kind === kind && h.id === id) ?? { kind, id, label, hint: 'Unlock this through play.' }
  );
  const inspect = (kind: LockedCosmeticInfo['kind'], id: number | string, label: string) => {
    onInspectLocked?.(hintFor(kind, id, label));
  };

  return (
    <div className="builder">
      <Section label="Body shape">
        <div className="builder-grid builder-grid-tight">
          {BODY_SHAPES.map((b, i) => (
            <BuilderTile
              key={b.id}
              selected={v.body === i}
              label={b.label}
              locked={!canBody(i)}
              onClick={() => canBody(i) ? update({ body: i }) : inspect('body', i, b.label)}
            >
              <IconAvatar icon={{ ...v, body: i, hat: 0 }} size={48} />
            </BuilderTile>
          ))}
        </div>
        {/* Archetypes — Pixi-rendered. The picker can't draw the
            silhouette here, so each tile shows a colored placeholder
            with a Mythic/Rare label cue. The actual body draws
            correctly in-game once equipped. */}
        <div className="builder-grid builder-grid-tight" style={{ marginTop: '0.5rem' }}>
          {BODY_ARCHETYPES.map((a) => (
            <BuilderTile
              key={`arch-${a.id}`}
              selected={v.body === a.id}
              label={`${a.label} ${a.rarity === 4 ? '· Mythic' : '· Rare'}`}
              locked={!canBody(a.id)}
              onClick={() => canBody(a.id) ? update({ body: a.id }) : inspect('body', a.id, a.label)}
            >
              <span style={{
                display: 'inline-block',
                width: 48, height: 48,
                borderRadius: '50%',
                background: a.color,
                border: a.rarity === 4 ? '2px solid #ffd770' : '2px solid #4a9eff',
                boxShadow: a.rarity === 4 ? '0 0 8px rgba(255,215,112,0.4)' : 'none',
              }} aria-hidden="true" />
            </BuilderTile>
          ))}
        </div>
      </Section>

      <Section label="Palette (3-color override)">
        <p className="muted" style={{ margin: '0 0 0.4rem', fontSize: '0.78rem' }}>
          Palettes override your body + accent hues with a curated 3-color set.
          "None" falls back to the hue swatches below.
        </p>
        <div className="hue-row">
          <button
            type="button"
            className={`hue-cell hue-cell-off${!v.paletteId ? ' selected' : ''}`}
            onClick={() => {
              // Clear by emitting a copy without paletteId.
              const { paletteId: _drop, ...rest } = v;
              void _drop;
              onChange(rest);
            }}
            aria-label="No palette (use hue swatches below)"
            title="None — use hue swatches"
          >
            <span style={{ fontSize: '0.7rem' }}>none</span>
          </button>
          {PALETTE_SWATCHES.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`hue-cell${v.paletteId === p.id ? ' selected' : ''}${canPalette(p.id) ? '' : ' locked'}`}
              onClick={() => canPalette(p.id)
                ? update({ paletteId: p.id })
                : inspect('palette' as LockedCosmeticInfo['kind'], p.id, p.label)}
              style={{ background: p.gradient, position: 'relative' }}
              aria-label={canPalette(p.id) ? `${p.label} palette` : `${p.label} palette locked`}
              title={p.label}
            >
              {p.rarity === 4 && (
                <span style={{
                  position: 'absolute', bottom: 1, right: 2, fontSize: 8,
                  color: '#ffd770', textShadow: '0 0 2px #000',
                }}>★</span>
              )}
            </button>
          ))}
        </div>
      </Section>

      <Section label="Headwear">
        <div className="builder-grid builder-grid-tight">
          {HAT_SHAPES.map((h, i) => (
            <BuilderTile
              key={h.id}
              selected={v.hat === i}
              label={h.label}
              locked={!canHat(i)}
              onClick={() => canHat(i) ? update({ hat: i }) : inspect('hat', i, h.label)}
            >
              <IconAvatar icon={{ ...v, hat: i }} size={48} />
            </BuilderTile>
          ))}
        </div>
      </Section>

      <Section label="Face">
        <div className="builder-grid builder-grid-tight">
          {FACE_SHAPES.map((f, i) => (
            <BuilderTile
              key={f.id}
              selected={v.face === i}
              label={f.label}
              locked={!canFace(i)}
              onClick={() => canFace(i) ? update({ face: i }) : inspect('face', i, f.label)}
            >
              <IconAvatar icon={{ ...v, face: i }} size={48} />
            </BuilderTile>
          ))}
        </div>
      </Section>

      <Section label="Body color">
        <div className="hue-row">
          {HUES.map((hue) => (
            <button
              key={`b-${hue}`}
              type="button"
              className={`hue-cell${v.hue === hue ? ' selected' : ''}${canHue(hue) ? '' : ' locked'}`}
              onClick={() => canHue(hue) ? update({ hue }) : inspect('hue', hue, `Hue ${hue}`)}
              style={{ background: `hsl(${hue} 70% 56%)` }}
              aria-label={canHue(hue) ? `body hue ${hue}` : `body hue ${hue} locked`}
            />
          ))}
        </div>
      </Section>

      <Section label="Accent (hat / blush)">
        <div className="hue-row">
          {HUES.map((hue) => (
            <button
              key={`a-${hue}`}
              type="button"
              className={`hue-cell${v.accentHue === hue ? ' selected' : ''}${canAccentHue(hue) ? '' : ' locked'}`}
              onClick={() => canAccentHue(hue) ? update({ accentHue: hue }) : inspect('accentHue', hue, `Accent hue ${hue}`)}
              style={{ background: `hsl(${hue} 75% 60%)` }}
              aria-label={canAccentHue(hue) ? `accent hue ${hue}` : `accent hue ${hue} locked`}
            />
          ))}
        </div>
      </Section>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="builder-section">
      <div className="builder-label muted">{label}</div>
      {children}
    </div>
  );
}

function BuilderTile({ selected, locked = false, label, onClick, children }: {
  selected: boolean; locked?: boolean; label: string; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`builder-cell${selected ? ' selected' : ''}${locked ? ' locked' : ''}`}
      onClick={onClick}
      aria-label={label}
      title={locked ? `${label} locked - click to see how to unlock` : label}
    >
      {children}
      {locked && <span className="lock-mark">Locked</span>}
    </button>
  );
}
